<template>
  <NodeViewWrapper>
    <div
      data-paragraph-section
      class="border rounded-md border-dashed p-5 mb-2"
      :class="props.node.attrs.optional ? 'border-blue-500' : 'border-green-500'"
    >
      <div contenteditable="false" class="flex items-baseline mb-5 border-b pb-5 gap-5">
        <div>
          <label class="text-sm font-bold mr-2">Titel: </label>
          <input
            class="text-sm border rounded-md px-2 py-1"
            :class="hasValidationError ? 'border-red-500 bg-red-50' : 'border-slate-300'"
            :value="props.node.attrs.label"
            @input="setLabel"
            @blur="validateLabel"
            placeholder="Titel der Sektion"
          />
          <div v-if="hasValidationError" class="text-red-500 text-xs mt-1">
            {{ validationError?.message }}
          </div>
        </div>

        <div class="flex flex-wrap gap-5">
          <div>
            <input
              type="checkbox"
              class="border border-dotted border-slate-300 pl-3"
              :checked="props.node.attrs.optional"
              @input="setOptional"
            />
            Optional
          </div>

          <div>
            <input
              type="checkbox"
              class="border border-dotted border-slate-300 pl-3"
              :checked="props.node.attrs['numbered-paragraph']"
              @input="setNumberedParagraph"
            />
            Numbered Paragraph
          </div>
        </div>

        <div class="ml-auto inline-flex align-middle">
          <LSButton size="small" class="delete-button mr-4" contenteditable="false" variant="filled" @click="removeSectionClick">
            <TrashIcon class="mb-2 delete-button ml-auto" />
          </LSButton>

          <div class="drag-handle" contenteditable="false" draggable="true" data-drag-handle></div>
        </div>
      </div>
      <NodeViewContent
        :data-editor-numbered-paragraph-section="props.node.attrs['numbered-paragraph']"
        :data-numbered-paragraph-value="props.node.attrs['numbered-paragraph-value']"
      ></NodeViewContent>
    </div>
    <div class="relative flex py-5 items-center" contenteditable="false">
      <div class="flex-grow border-t border-gray-400"></div>
      <span class="flex-shrink mx-4 text-gray-400">
        <LSButton size="small" class="mb-2" contenteditable="false" variant="outline" @click="onClick">Add section</LSButton>
      </span>
      <div class="flex-grow border-t border-gray-400"></div>
    </div>

    <DialogModal
      :show="dialogData.isOpen"
      title=""
      :description="dialogData.description"
      :action-buttons="[
        { onClick: dialogData.accept, type: 'danger', label: 'Delete' },
        { onClick: dialogData.cancel, type: 'secondary', label: 'Cancel' },
      ]"
    >
    </DialogModal>
  </NodeViewWrapper>
</template>

<script setup lang="ts">
import { NodeViewContent, NodeViewWrapper, nodeViewProps } from '@tiptap/vue-3'
import TrashIcon from '@/assets/icons/24/trash.svg?component'
import LSButton from '@/components/LSButton.vue'
import { onMounted, ref, computed, inject } from 'vue'
import { useLsGeneralStore } from '@/questionnaire/stores/application'
import DialogModal from '@/components/DialogModal.vue'
import { v4 as uuidv4 } from 'uuid'
import type { useSectionValidation } from './composables/useSectionValidation'

const props = defineProps(nodeViewProps)
const { env } = useLsGeneralStore()
const sectionValidation = inject<ReturnType<typeof useSectionValidation>>('sectionValidation')

const dialogData = ref({
  isOpen: false,
  description: '',
  accept: () => {},
  cancel: () => {},
})

// Validation computed properties
const validationError = computed(() => {
  if (!sectionValidation) return null
  return sectionValidation.getSectionError(props.node.attrs.code)
})

const hasValidationError = computed(() => {
  return validationError.value !== undefined
})
const onClick = () => {
  const endPos = props.getPos() + props.node.nodeSize

  // I focus the start of the editor because
  // when the cursor is at the end of the node below which
  // we want to add a block, it doesn't focus the next block
  // props.node.commands.focus('start')
  props.editor.commands.focus('start')

  const newSectionText = 'new section'

  props.editor
    .chain()
    .insertContentAt(endPos, {
      type: 'section',
      attrs: {
        code: uuidv4(),
      },
      content: [{ type: 'paragraph', content: [{ type: 'text', text: newSectionText }] }],
    })
    .focus(endPos + newSectionText.length + 2)
    .run()
}

const removeSectionClick = () => {
  dialogData.value = {
    isOpen: true,
    description: 'You are about to delete a section. Would you like to proceed?',
    accept: () => {
      props.deleteNode()
    },
    cancel: () => {
      closeModal()
    },
  }
}

const closeModal = () => {
  dialogData.value = {
    isOpen: false,
    description: '',
    accept: () => {},
    cancel: () => {},
  }
}

const setOptional = (e: Event) => {
  const target = e.target as HTMLInputElement
  props.updateAttributes({
    optional: target.checked,
  })
}

const setNumberedParagraph = (e: Event) => {
  const target = e.target as HTMLInputElement
  props.updateAttributes({
    ['numbered-paragraph']: target.checked,
  })
}

const setLabel = (e: Event) => {
  const target = e.target as HTMLInputElement
  props.updateAttributes({
    label: target.value,
  })

  // Clear validation error when user starts typing
  if (sectionValidation && target.value.trim() !== '') {
    sectionValidation.clearValidationErrors()
  }
}

const validateLabel = () => {
  if (!sectionValidation || !props.editor) return

  // Trigger validation for this specific section
  sectionValidation.validateSections(props.editor)
}

onMounted(() => {
  if (env?.DEV) {
    console.log(props.getPos(), props.node.attrs.code)
  }
})
</script>

<style scoped>
.drag-handle {
  flex: 0 0 auto;
  position: relative;
  width: 1rem;
  height: 1rem;
  top: 0.3rem;
  margin-right: 0.5rem;
  cursor: grab;
  background-image: url('data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 16"><path fill-opacity="0.2" d="M4 14c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zM2 6C.9 6 0 6.9 0 8s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6C.9 0 0 .9 0 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" /></svg>');
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
}
</style>
